import { createApp } from "vue";

import App from "./App.vue";
import { onUnauthorized } from "./api/client";
import { APP_VIEWS, createAppRouter } from "./router";
import { setI18nLanguage, translate } from "./stores/i18n";
import { loadSession, loginUrl, session } from "./stores/session";

const router = createAppRouter();
document.documentElement.classList.remove("i18n-pending");
let redirecting = false;
onUnauthorized(() => {
  if (redirecting) return;
  redirecting = true;
  session.user = null;
  window.location.assign(loginUrl());
});

const routeTitles = {
  maya: "Maya",
  overview: "Overview",
  alerts: "Alerts",
  reports: "Reports",
  sites: "Sites",
  plan: "90-day plan",
  files: "Deliverables",
  profile: "Brand profile",
  crawlers: "AI crawler insights",
  competitors: "Competitors",
  citations: "Citations",
  prompts: "Prompts",
  sentiment: "Sentiment",
  studio: "Studio",
  settings: "Account settings",
  email: "Change email",
  orders: "Orders",
  billing: "Billing and plan",
  partner: "Partner Program",
};

router.beforeEach(async (to) => {
  let user = null;
  try {
    user = await loadSession();
    await setI18nLanguage(session.language);
  } catch {
    user = null;
  }
  if (!user) {
    window.location.assign(loginUrl());
    return false;
  }
  if (to.path === "/" || to.path === "") {
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (APP_VIEWS.includes(hash)) {
      return { path: `/${hash}`, query: to.query, replace: true };
    }
    return { path: "/maya", query: to.query, replace: true };
  }
  if (to.path === "/studio" || to.path.startsWith("/account/") || to.path === "/articles/new") return true;
  const view = String(to.params.view || to.path.replace(/^\/+/, "").split("/")[0] || "");
  if (!APP_VIEWS.includes(view)) {
    return { path: "/maya", query: to.query, replace: true };
  }
  return true;
});
router.afterEach((to, from) => {
  const key = String(to.params.account || to.params.view || to.path.split("/")[1] || "maya");
  const titleKey = { reports: "reports_h", plan: "plan_page_title", distribution: "dist_h", files: "files_h", alerts: "alerts_h", sites: "sites_h", crawlers: "cw_h", competitors: "comp_h", citations: "cit_h", prompts: "pr_h", sentiment: "sen_h", profile: "prof_h", partner: "partner_page_title" }[key];
  document.title = `${(titleKey && translate(titleKey)) || routeTitles[key] || to.meta.title || "Dashboard"} · SoaRank`;
  document.documentElement.lang = session.language || "en";
  let robots = document.head.querySelector('meta[name="robots"]');
  if (!robots) { robots = document.createElement("meta"); robots.name = "robots"; document.head.appendChild(robots); }
  robots.content = "noindex, nofollow";
  // The initial route replaces the server-rendered loader node. Focusing the new
  // main element for that one transition briefly paints its focus ring before Maya
  // focuses the composer; subsequent client-side navigations still announce the page.
  if (!from.matched.length) return;
  requestAnimationFrame(() => document.getElementById("main-content")?.focus({ preventScroll: true }));
});

const app = createApp(App);
app.use(router);
router.isReady().then(() => {
  app.mount("#app");
});
